import { Controller } from "@hotwired/stimulus"

// Admin Login Controller
// Handles captcha integration for admin login form
// Supports both Turnstile (embedded) and Tencent (popup) providers
//
// Usage:
// <form data-controller="admin-login"
//       data-admin-login-provider-value="turnstile"
//       data-admin-login-site-key-value="0x4AAA...">
//   <div data-admin-login-target="widget"></div>
//   <input type="hidden" name="captcha_ticket" data-admin-login-target="ticket">
//   <input type="hidden" name="captcha_randstr" data-admin-login-target="randstr">
//   <button type="submit" data-admin-login-target="submit">Login</button>
// </form>

export default class extends Controller {
  static targets = ["widget", "ticket", "randstr", "submit", "error"]

  static values = {
    siteKey: String,
    provider: { type: String, default: "" }
  }

  connect() {
    // Skip if captcha not configured
    if (!this.providerValue || !this.siteKeyValue) return

    if (this.providerValue === "turnstile") {
      this.loadTurnstile()
    } else if (this.providerValue === "tencent") {
      this.loadTencent()
    }
  }

  disconnect() {
    if (this.providerValue === "turnstile" && this.widgetId != null && window.turnstile) {
      try {
        window.turnstile.remove(this.widgetId)
      } catch (e) {
        // Widget might already be removed
      }
    }
  }

  // ============== Turnstile (Embedded Widget) ==============

  loadTurnstile() {
    if (window.turnstile) {
      this.renderTurnstileWidget()
      return
    }

    // Prevent duplicate script loading during Turbo Drive navigation
    const existingScript = document.querySelector('script[src*="turnstile"]')
    if (existingScript) {
      existingScript.addEventListener('load', () => this.renderTurnstileWidget(), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => this.renderTurnstileWidget()
    script.onerror = () => this.showError(this.t('loadFailed'))
    document.head.appendChild(script)
  }

  renderTurnstileWidget() {
    if (!this.hasWidgetTarget) return

    try {
      this.widgetId = window.turnstile.render(this.widgetTarget, {
        sitekey: this.siteKeyValue,
        theme: 'auto',
        callback: (token) => this.handleTurnstileSuccess(token),
        'error-callback': () => this.handleTurnstileError(),
        'expired-callback': () => this.handleTurnstileExpired()
      })
    } catch (e) {
      console.error('Failed to render Turnstile widget:', e)
      this.showError(this.t('initFailed'))
    }
  }

  handleTurnstileSuccess(token) {
    if (this.hasTicketTarget) {
      this.ticketTarget.value = token
    }
    this.hideError()
    // Auto-submit the form (consistent with Tencent behavior)
    this.element.closest('form')?.submit()
  }

  handleTurnstileError() {
    if (this.hasTicketTarget) {
      this.ticketTarget.value = ''
    }
    this.showError(this.t('verifyFailed'))
  }

  handleTurnstileExpired() {
    if (this.hasTicketTarget) {
      this.ticketTarget.value = ''
    }
    if (this.widgetId != null && window.turnstile) {
      window.turnstile.reset(this.widgetId)
    }
  }

  // ============== Tencent (Popup Modal) ==============

  loadTencent() {
    if (window.TencentCaptcha) return

    // Prevent duplicate script loading during Turbo Drive navigation
    if (document.querySelector('script[src*="TJCaptcha"]')) return

    const script = document.createElement('script')
    script.src = 'https://turing.captcha.qcloud.com/TJCaptcha.js'
    script.async = true
    script.onerror = () => this.showError(this.t('loadFailed'))
    document.head.appendChild(script)
  }

  showTencentCaptcha(event) {
    event.preventDefault()

    if (!window.TencentCaptcha) {
      this.showError(this.t('loadFailed'))
      return
    }

    const captcha = new window.TencentCaptcha(
      this.siteKeyValue,
      (res) => this.handleTencentCallback(res),
      { needFeedBack: false }
    )

    captcha.show()
  }

  handleTencentCallback(res) {
    if (res.ret === 0) {
      // Success
      if (this.hasTicketTarget) {
        this.ticketTarget.value = res.ticket
      }
      if (this.hasRandstrTarget) {
        this.randstrTarget.value = res.randstr
      }
      this.hideError()
      // Auto-submit the form
      this.element.closest('form')?.submit()
    } else if (res.ret === 2) {
      // User cancelled - do nothing
    } else {
      this.showError(this.t('verifyFailed'))
    }
  }

  // ============== Form Submission ==============

  submitForm(event) {
    // Skip validation if captcha not configured
    if (!this.providerValue || !this.siteKeyValue) {
      return true
    }

    if (this.providerValue === "turnstile") {
      // Turnstile: check if token is filled
      if (!this.hasTicketTarget || !this.ticketTarget.value) {
        event.preventDefault()
        this.showError(this.t('pleaseComplete'))
        return false
      }
    } else if (this.providerValue === "tencent") {
      // Tencent: show popup if not verified
      if (!this.hasTicketTarget || !this.ticketTarget.value) {
        this.showTencentCaptcha(event)
        return false
      }
    }

    return true
  }

  // ============== UI Helpers ==============

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  // Simple i18n based on document language
  t(key) {
    const lang = document.documentElement.lang || 'zh'
    const messages = {
      zh: {
        loadFailed: '验证服务加载失败',
        initFailed: '验证服务初始化失败',
        verifyFailed: '验证失败，请重试',
        pleaseComplete: '请完成安全验证'
      },
      en: {
        loadFailed: 'Failed to load verification service',
        initFailed: 'Failed to initialize verification',
        verifyFailed: 'Verification failed, please try again',
        pleaseComplete: 'Please complete security verification'
      }
    }
    const strings = messages[lang] || messages['en']
    return strings[key] || key
  }
}
